/**
 * BroadcastDirector — the /watch camera state machine.
 *
 * Mounts inside <Canvas> ONLY in broadcast mode (see Lobby's `broadcast` prop),
 * where it is the sole camera writer: the player rig is absent and
 * AerialCamera/SpectatorCamera stay inactive. It subscribes to the shared
 * broadcast event bus and cuts between cinematic shots with priority + minimum
 * hold so it never seizure-cuts, lerping the camera between shots for smooth
 * moves. It writes the active shot to the store so the broadcast HUD (and a
 * future narrator) can react.
 *
 * Shot rigs are tuned to Lobby.tsx's hall placements (cauldron at z≈3.8, throne
 * at the −z wall). Adjust the constants below to reframe.
 */
import { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

import { useStore, type BroadcastShot } from "../store";
import type { KothRoundState } from "../games/last-touch/useKothRound";
import { useBroadcastEvents } from "./useBroadcastEvents";

interface ShotSpec {
  prio: number;
  /** Min seconds to hold before a same-or-lower-priority event can cut away. */
  minHold: number;
  /** Max seconds before auto-falling-through to FALLBACK. */
  maxHold: number;
}

const SPEC: Record<BroadcastShot, ShotSpec> = {
  establish: { prio: 0, minHold: 0, maxHold: Infinity },
  dead_air: { prio: 1, minHold: 3, maxHold: Infinity },
  pot_spike: { prio: 2, minHold: 3.5, maxHold: 4.5 },
  throne_taken: { prio: 3, minHold: 4, maxHold: 5.5 },
  endgame: { prio: 4, minHold: 0, maxHold: Infinity },
  winner: { prio: 5, minHold: 7, maxHold: 9 },
};

const FALLBACK: Record<BroadcastShot, BroadcastShot> = {
  establish: "establish",
  dead_air: "dead_air",
  pot_spike: "establish",
  throne_taken: "establish",
  endgame: "endgame",
  winner: "establish",
};

// Hall landmarks (world space), matched to Lobby.tsx placements. The hall is a
// CLOSED box: x∈[-14,14], z∈[-8 (back wall, behind throne), 26 (entrance)],
// height 13. Every camera below MUST stay inside that box — a camera outside a
// wall is blocked by it (the "filming from outside in" bug). Keep ≥1m margin.
const CAULDRON = new THREE.Vector3(0, 2.0, 3.8); // the pot
const KING_LOOK = new THREE.Vector3(0, 2.4, 0.0); // the crowned king on the throne
// What the idle shots aim at — the action zone between throne (z=0) and pot
// (z=3.8), framed from inside the hall (not pulled out past the walls).
const HALL_CENTER = new THREE.Vector3(0, 2.5, 2.5);

export function BroadcastDirector({
  round,
}: {
  round: KothRoundState | null;
}): null {
  const camera = useThree((s) => s.camera);
  const setBroadcastShot = useStore((s) => s.setBroadcastShot);
  const envelope = useBroadcastEvents(round);

  const shotRef = useRef<BroadcastShot>("establish");
  const heldRef = useRef(0);
  const angleRef = useRef(0);
  const hasRoundRef = useRef<boolean>(round !== null);

  const lookRef = useRef(new THREE.Vector3(0, 3, 0));
  const tmpPos = useMemo(() => new THREE.Vector3(), []);
  const tmpLook = useMemo(() => new THREE.Vector3(), []);

  const setShot = (s: BroadcastShot): void => {
    if (shotRef.current === s) return;
    shotRef.current = s;
    heldRef.current = 0;
    if (s === "endgame") angleRef.current = 0;
    setBroadcastShot(s);
  };

  // Claim / release the store's broadcast-camera ownership flag.
  useEffect(() => {
    setBroadcastShot("establish");
    shotRef.current = "establish";
    heldRef.current = 0;
    return () => setBroadcastShot(null);
  }, [setBroadcastShot]);

  useEffect(() => {
    hasRoundRef.current = round !== null;
  }, [round]);

  // React to the latest event with priority + min-hold gating.
  useEffect(() => {
    if (!envelope) return;
    const e = envelope.event;
    let target: BroadcastShot | null = null;
    switch (e.kind) {
      case "settled":
        target = "winner";
        break;
      case "endgame":
        target = "endgame";
        break;
      case "king_change":
        target = "throne_taken";
        break;
      case "pot_spike":
        target = "pot_spike";
        break;
      case "touch":
        target = null; // emitted for the narrator; not worth a cut on its own
        break;
    }
    if (!target) return;
    const cur = shotRef.current;
    if (
      SPEC[target]!.prio >= SPEC[cur]!.prio ||
      heldRef.current >= SPEC[cur]!.minHold
    ) {
      setShot(target);
    }
    // setShot/SPEC are stable; we intentionally only react to a new envelope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envelope]);

  useFrame((_, dt) => {
    const step = Math.min(dt, 0.1); // swallow tab-out delta spikes
    heldRef.current += step;

    let shot = shotRef.current;

    // No live round → idle on the wide shot (unless still holding a winner card).
    if (!hasRoundRef.current && shot !== "winner" && shot !== "dead_air") {
      setShot("dead_air");
      shot = "dead_air";
    }

    // Max-hold fall-through.
    if (heldRef.current >= SPEC[shot]!.maxHold) {
      const fb = FALLBACK[shot]!;
      if (fb !== shot) {
        setShot(fb);
        shot = fb;
      }
    }

    rigFor(shot, step, angleRef, tmpPos, tmpLook);

    const k = 1 - Math.exp(-2.5 * step);
    camera.position.lerp(tmpPos, k);
    lookRef.current.lerp(tmpLook, k);
    camera.lookAt(lookRef.current);
  });

  return null;
}

/** Write the shot's target position + look-at into the scratch vectors. */
function rigFor(
  shot: BroadcastShot,
  dt: number,
  angleRef: { current: number },
  outPos: THREE.Vector3,
  outLook: THREE.Vector3,
): void {
  switch (shot) {
    case "pot_spike":
      // Close on the pot from the audience side.
      outPos.set(0, 3.4, 9.0);
      outLook.copy(CAULDRON);
      break;
    case "throne_taken":
      // Elevated 3/4 angle on the king — off the centre axis so the tall pot-
      // banner hologram (at x≈0) falls to the side, and high enough to look
      // over the cauldron (z=3.8) instead of through it.
      outPos.set(4.0, 4.5, 5.5);
      outLook.copy(KING_LOOK);
      break;
    case "winner":
      // Hero 3/4 from the other side, slightly lower + closer — same off-axis,
      // over-the-cauldron framing so the crowned king reads clearly.
      outPos.set(-3.5, 4.0, 5.2);
      outLook.copy(KING_LOOK);
      break;
    case "dead_air":
      // Wide-ish static from inside the hall, looking down the carpet at the
      // throne/pot — shows depth + any players without leaving the box.
      outPos.set(0, 6.5, 15);
      outLook.copy(HALL_CENTER);
      break;
    case "endgame": {
      // Tension orbit around the throne, kept fully inside the back half of the
      // hall (radius 5.5 about z=1.5 → z∈[-4,7], never crosses the back wall).
      angleRef.current += dt * 0.16;
      const a = angleRef.current;
      outPos.set(Math.cos(a) * 5.5, 4.5, 1.5 + Math.sin(a) * 5.5);
      outLook.copy(KING_LOOK);
      break;
    }
    case "establish":
    default: {
      // Slow orbit over the action zone, inside the walls (radius 8 about z=4 →
      // z∈[-4,12], x∈[-8,8]) — frames throne + pot, never clips a wall.
      angleRef.current += dt * 0.08;
      const a = angleRef.current;
      outPos.set(Math.cos(a) * 8, 6, 4 + Math.sin(a) * 8);
      outLook.copy(HALL_CENTER);
      break;
    }
  }
}
